import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Gift,
  Loader2,
  Percent,
  Search,
  ShoppingBag,
  Sparkles,
  Tag,
  Wallet,
  X,
} from 'lucide-react';
import { posApi, promotionsApi } from '@/services/api';
import PromotionScopeSummary from '@/components/promotions/PromotionScopeSummary';
import {
  describeOffer,
  type PromotionCampaign,
  type PromotionChannelScope,
  type PromotionCustomerType,
  type PromotionRule,
  type PromotionRuleType,
  type PromotionScope,
} from '@/utils/promotions';

interface PosItemOption {
  name: string;
  item_name: string;
  item_type?: string;
}

interface CategoryOption {
  name: string;
  category_name: string;
  category_type?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editing: PromotionCampaign | null;
}

const WHO: { id: PromotionCustomerType; labelKey: string; helpKey: string }[] = [
  { id: 'Retailer', labelKey: 'promotions.who.retailer', helpKey: 'promotions.who.retailerHelp' },
  { id: 'All', labelKey: 'promotions.who.all', helpKey: 'promotions.who.allHelp' },
  { id: 'Wholesale', labelKey: 'promotions.who.wholesale', helpKey: 'promotions.who.wholesaleHelp' },
  { id: 'Distributor', labelKey: 'promotions.who.distributor', helpKey: 'promotions.who.distributorHelp' },
];

const CHANNELS: { id: PromotionChannelScope; labelKey: string; helpKey: string }[] = [
  { id: 'POS', labelKey: 'promotions.channel.pos', helpKey: 'promotions.channel.posHelp' },
  { id: 'Web', labelKey: 'promotions.channel.web', helpKey: 'promotions.channel.webHelp' },
  { id: 'Both', labelKey: 'promotions.channel.both', helpKey: 'promotions.channel.bothHelp' },
];

const OFFER_TYPES: { id: PromotionRuleType; icon: typeof Percent; labelKey: string; helpKey: string }[] = [
  { id: 'Percent Off', icon: Percent, labelKey: 'promotions.offer.percent', helpKey: 'promotions.offer.percentHelp' },
  { id: 'Buy X Get Y', icon: Gift, labelKey: 'promotions.offer.buyx', helpKey: 'promotions.offer.buyxHelp' },
  { id: 'Amount Off', icon: Wallet, labelKey: 'promotions.offer.amount', helpKey: 'promotions.offer.amountHelp' },
  { id: 'Special Price', icon: Tag, labelKey: 'promotions.offer.special', helpKey: 'promotions.offer.specialHelp' },
  { id: 'Quantity Break', icon: ShoppingBag, labelKey: 'promotions.offer.qty', helpKey: 'promotions.offer.qtyHelp' },
  { id: 'Cart Threshold', icon: Sparkles, labelKey: 'promotions.offer.cart', helpKey: 'promotions.offer.cartHelp' },
];

function toLocalInput(value?: string | null): string {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function defaultDates(): { from: string; to: string } {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { from: toLocalInput(start.toISOString()), to: toLocalInput(end.toISOString()) };
}

function emptyRule(type: PromotionRuleType = 'Percent Off'): PromotionRule {
  return {
    rule_name: '',
    is_active: true,
    rule_type: type,
    apply_scope: 'All',
    discount_percent: type === 'Percent Off' ? 20 : type === 'Quantity Break' ? 10 : 0,
    discount_amount: type === 'Amount Off' ? 50 : 0,
    special_price: 0,
    min_qty: type === 'Quantity Break' ? 5 : 0,
    buy_qty: 2,
    get_qty: 1,
    get_discount_percent: 100,
    min_cart_amount: type === 'Cart Threshold' ? 1000 : 0,
    cart_discount_percent: type === 'Cart Threshold' ? 10 : 0,
    cart_discount_amount: 0,
    priority: 100,
    rule_categories: [],
    rule_items: [],
  };
}

function emptyCampaign(): PromotionCampaign {
  const dates = defaultDates();
  return {
    campaign_name: '',
    is_active: true,
    occasion: 'Custom',
    valid_from: fromLocalInput(dates.from),
    valid_to: fromLocalInput(dates.to),
    customer_types: 'Retailer',
    channel_scope: 'POS',
    banner_text: '',
    priority: 100,
    rules: [emptyRule('Percent Off')],
  };
}

export default function PromotionWizardModal({ isOpen, onClose, editing }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<PromotionCampaign>(emptyCampaign());
  const [fromLocal, setFromLocal] = useState('');
  const [toLocal, setToLocal] = useState('');
  const [error, setError] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setForm({
        ...emptyCampaign(),
        ...editing,
        rules: editing.rules?.length
          ? editing.rules.map((r) => ({
              ...emptyRule(r.rule_type),
              ...r,
              rule_categories: r.rule_categories?.length
                ? r.rule_categories
                : (r.categories || []).map((id, i) => ({
                    item_category: id,
                    category_name: r.category_names?.[i] || id,
                  })),
              rule_items: r.rule_items?.length
                ? r.rule_items
                : (r.items || []).map((id, i) => ({
                    item: id,
                    item_name: r.item_names?.[i] || id,
                  })),
            }))
          : [emptyRule()],
      });
      setFromLocal(toLocalInput(editing.valid_from));
      setToLocal(toLocalInput(editing.valid_to));
    } else {
      const dates = defaultDates();
      setForm(emptyCampaign());
      setFromLocal(dates.from);
      setToLocal(dates.to);
    }
    setStep(1);
    setError('');
    setItemSearch('');
    setCategorySearch('');
  }, [isOpen, editing]);

  const rule = form.rules[0] || emptyRule();
  const setRule = (patch: Partial<PromotionRule>) => {
    const next = { ...rule, ...patch };
    if (patch.rule_type && patch.rule_type !== rule.rule_type) {
      Object.assign(next, emptyRule(patch.rule_type), { rule_name: rule.rule_name });
    }
    setForm({ ...form, rules: [next, ...form.rules.slice(1)] });
  };

  const { data: categories = [] } = useQuery({
    queryKey: ['promotionCategories'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message as CategoryOption[];
    },
    enabled: isOpen,
  });

  const { data: itemResults = [], isFetching: itemsLoading } = useQuery({
    queryKey: ['promotionItems', itemSearch],
    queryFn: async () => {
      const res = await posApi.getItems({ search: itemSearch.trim(), limit: 30 });
      const payload = res.data.message as { items?: PosItemOption[] } | PosItemOption[];
      return (Array.isArray(payload) ? payload : payload?.items ?? []) as PosItemOption[];
    },
    enabled: isOpen && rule.apply_scope === 'Items' && itemSearch.trim().length >= 2,
  });

  const selectedCategoryIds = useMemo(
    () => new Set((rule.rule_categories || []).map((row) => row.item_category)),
    [rule.rule_categories]
  );

  const selectedItemIds = useMemo(
    () => new Set((rule.rule_items || []).map((row) => row.item)),
    [rule.rule_items]
  );

  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    return categories.filter((cat) => {
      if (selectedCategoryIds.has(cat.name)) return false;
      if (!q) return true;
      return (
        (cat.category_name || cat.name).toLowerCase().includes(q) ||
        cat.name.toLowerCase().includes(q) ||
        (cat.category_type || '').toLowerCase().includes(q)
      );
    });
  }, [categories, categorySearch, selectedCategoryIds]);

  const handleScopeChange = (scope: PromotionScope) => {
    setRule({
      apply_scope: scope,
      rule_categories: scope === 'Categories' ? rule.rule_categories || [] : [],
      rule_items: scope === 'Items' ? rule.rule_items || [] : [],
    });
    setItemSearch('');
    setCategorySearch('');
  };

  const addRuleCategory = (cat: CategoryOption) => {
    if (selectedCategoryIds.has(cat.name)) return;
    setRule({
      rule_categories: [
        ...(rule.rule_categories || []),
        { item_category: cat.name, category_name: cat.category_name || cat.name },
      ],
    });
  };

  const removeRuleCategory = (categoryId: string) => {
    setRule({
      rule_categories: (rule.rule_categories || []).filter((row) => row.item_category !== categoryId),
    });
  };

  const addRuleItem = (item: PosItemOption) => {
    if (selectedItemIds.has(item.name)) return;
    setRule({
      rule_items: [
        ...(rule.rule_items || []),
        { item: item.name, item_name: item.item_name || item.name },
      ],
    });
  };

  const removeRuleItem = (itemId: string) => {
    setRule({
      rule_items: (rule.rule_items || []).filter((row) => row.item !== itemId),
    });
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      promotionsApi.save({
        ...form,
        valid_from: fromLocalInput(fromLocal),
        valid_to: fromLocalInput(toLocal),
        rules: form.rules.map((r) => ({
          ...r,
          rule_name: r.rule_name || describeOffer(r),
        })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promotionCampaigns'] });
      void qc.invalidateQueries({ queryKey: ['posPromotions'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : t('promotions.saveFailed'));
    },
  });

  if (!isOpen) return null;

  const canNext = () => {
    if (step === 1) return Boolean(form.campaign_name.trim() && fromLocal && toLocal);
    if (step === 2) {
      if (rule.apply_scope === 'Categories' && !(rule.rule_categories || []).length) return false;
      if (rule.apply_scope === 'Items' && !(rule.rule_items || []).length) return false;
      return true;
    }
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {editing ? t('promotions.editSale') : t('promotions.newSale')}
            </h2>
            <p className="text-sm text-gray-500">{t(`promotions.step${step}Title`)}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3 flex gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-primary-600' : 'bg-gray-200'}`} />
          ))}
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
          )}

          {step === 1 && (
            <>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">{t('promotions.saleName')}</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.campaign_name}
                  onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
                  placeholder={t('promotions.saleNamePlaceholder')}
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">{t('promotions.starts')}</span>
                  <input
                    type="datetime-local"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={fromLocal}
                    onChange={(e) => setFromLocal(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">{t('promotions.ends')}</span>
                  <input
                    type="datetime-local"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={toLocal}
                    onChange={(e) => setToLocal(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{t('promotions.whoTitle')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {WHO.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => setForm({ ...form, customer_types: w.id })}
                      className={`rounded-xl border p-3 text-left ${
                        form.customer_types === w.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{t(w.labelKey)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{t(w.helpKey)}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{t('promotions.channelTitle')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {CHANNELS.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => setForm({ ...form, channel_scope: ch.id })}
                      className={`rounded-xl border p-3 text-left ${
                        (form.channel_scope || 'POS') === ch.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{t(ch.labelKey)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{t(ch.helpKey)}</div>
                    </button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">{t('promotions.banner')}</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.banner_text || ''}
                  onChange={(e) => setForm({ ...form, banner_text: e.target.value })}
                  placeholder={t('promotions.bannerPlaceholder')}
                />
              </label>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-gray-600">{t('promotions.pickOffer')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {OFFER_TYPES.map((o) => {
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setRule({ rule_type: o.id })}
                      className={`rounded-xl border p-3 text-left ${
                        rule.rule_type === o.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-medium text-gray-900">
                        <Icon className="w-4 h-4 text-primary-600" />
                        {t(o.labelKey)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{t(o.helpKey)}</div>
                    </button>
                  );
                })}
              </div>

              {rule.rule_type === 'Percent Off' && (
                <NumberField
                  label={t('promotions.fields.percent')}
                  value={rule.discount_percent || 0}
                  onChange={(v) => setRule({ discount_percent: v })}
                />
              )}
              {rule.rule_type === 'Amount Off' && (
                <NumberField
                  label={t('promotions.fields.amount')}
                  value={rule.discount_amount || 0}
                  onChange={(v) => setRule({ discount_amount: v })}
                />
              )}
              {rule.rule_type === 'Special Price' && (
                <NumberField
                  label={t('promotions.fields.special')}
                  value={rule.special_price || 0}
                  onChange={(v) => setRule({ special_price: v })}
                />
              )}
              {rule.rule_type === 'Quantity Break' && (
                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label={t('promotions.fields.minQty')}
                    value={rule.min_qty || 0}
                    onChange={(v) => setRule({ min_qty: v })}
                  />
                  <NumberField
                    label={t('promotions.fields.percent')}
                    value={rule.discount_percent || 0}
                    onChange={(v) => setRule({ discount_percent: v })}
                  />
                </div>
              )}
              {rule.rule_type === 'Buy X Get Y' && (
                <div className="grid grid-cols-3 gap-3">
                  <NumberField
                    label={t('promotions.fields.buyQty')}
                    value={rule.buy_qty || 2}
                    onChange={(v) => setRule({ buy_qty: v })}
                  />
                  <NumberField
                    label={t('promotions.fields.getQty')}
                    value={rule.get_qty || 1}
                    onChange={(v) => setRule({ get_qty: v })}
                  />
                  <NumberField
                    label={t('promotions.fields.getPercent')}
                    value={rule.get_discount_percent || 100}
                    onChange={(v) => setRule({ get_discount_percent: v })}
                  />
                </div>
              )}
              {rule.rule_type === 'Cart Threshold' && (
                <div className="grid grid-cols-2 gap-3">
                  <NumberField
                    label={t('promotions.fields.spend')}
                    value={rule.min_cart_amount || 0}
                    onChange={(v) => setRule({ min_cart_amount: v })}
                  />
                  <NumberField
                    label={t('promotions.fields.billPercent')}
                    value={rule.cart_discount_percent || 0}
                    onChange={(v) => setRule({ cart_discount_percent: v })}
                  />
                </div>
              )}

              {rule.rule_type !== 'Cart Threshold' && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('promotions.applyTo')}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(['All', 'Categories', 'Items'] as PromotionScope[]).map((scope) => (
                      <button
                        key={scope}
                        type="button"
                        onClick={() => handleScopeChange(scope)}
                        className={`px-3 py-1.5 rounded-full text-sm ${
                          rule.apply_scope === scope
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(`promotions.scope.${scope.toLowerCase()}`)}
                      </button>
                    ))}
                  </div>
                  {rule.apply_scope === 'Categories' && (
                    <div className="space-y-3 rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                      <label className="text-sm font-medium text-gray-700">
                        {t('promotions.scope.selectCategories')}
                      </label>
                      <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          className="input-field pl-9"
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          placeholder={t('promotions.scope.searchCategories')}
                        />
                      </div>
                      {filteredCategories.length > 0 && (
                        <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                          {filteredCategories.map((cat) => (
                            <button
                              key={cat.name}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                              onClick={() => addRuleCategory(cat)}
                            >
                              <span className="text-sm text-gray-900">{cat.category_name || cat.name}</span>
                              {cat.category_type ? (
                                <span className="text-xs text-gray-500 shrink-0">{cat.category_type}</span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      )}
                      {categorySearch.trim() && filteredCategories.length === 0 && (
                        <p className="text-xs text-gray-500">{t('promotions.scope.noCategoriesFound')}</p>
                      )}
                      {(rule.rule_categories?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            {t('promotions.scope.selectedCategories')}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {(rule.rule_categories || []).map((row) => (
                              <span
                                key={row.item_category}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-800"
                              >
                                {row.category_name || row.item_category}
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-500"
                                  onClick={() => removeRuleCategory(row.item_category)}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {rule.apply_scope === 'Items' && (
                    <div className="space-y-3 rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                      <label className="text-sm font-medium text-gray-700">
                        {t('promotions.scope.selectProducts')}
                      </label>
                      <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          className="input-field pl-9"
                          value={itemSearch}
                          onChange={(e) => setItemSearch(e.target.value)}
                          placeholder={t('promotions.scope.searchProducts')}
                        />
                      </div>
                      {itemsLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          …
                        </div>
                      )}
                      {itemSearch.trim().length > 0 && itemSearch.trim().length < 2 && (
                        <p className="text-xs text-gray-500">{t('promotions.scope.typeToSearch')}</p>
                      )}
                      {itemSearch.trim().length >= 2 &&
                        itemResults.filter((item) => !selectedItemIds.has(item.name)).length > 0 && (
                          <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                            {itemResults
                              .filter((item) => !selectedItemIds.has(item.name))
                              .map((item) => (
                                <button
                                  key={item.name}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                                  onClick={() => addRuleItem(item)}
                                >
                                  <span className="text-sm text-gray-900">{item.item_name || item.name}</span>
                                  {item.item_type ? (
                                    <span className="text-xs text-gray-500 shrink-0">{item.item_type}</span>
                                  ) : null}
                                </button>
                              ))}
                          </div>
                        )}
                      {itemSearch.trim().length >= 2 &&
                        !itemsLoading &&
                        itemResults.filter((item) => !selectedItemIds.has(item.name)).length === 0 && (
                          <p className="text-xs text-gray-500">{t('promotions.scope.noProductsFound')}</p>
                        )}
                      {(rule.rule_items?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            {t('promotions.scope.selectedProducts')}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {(rule.rule_items || []).map((row) => (
                              <span
                                key={row.item}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-800"
                              >
                                {row.item_name || row.item}
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-500"
                                  onClick={() => removeRuleItem(row.item)}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-3">
              <p className="text-base font-semibold text-gray-900">{form.campaign_name}</p>
              <p className="text-sm text-gray-600">
                {t('promotions.reviewSummary', {
                  offer: describeOffer(rule),
                  who: t(`promotions.who.${(form.customer_types || 'Retailer').toLowerCase()}`),
                  from: fromLocal.replace('T', ' '),
                  to: toLocal.replace('T', ' '),
                })}
              </p>
              <PromotionScopeSummary rule={rule} maxChips={0} />
              <p className="text-sm text-emerald-700">{t('promotions.reviewSafe')}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t bg-white sticky bottom-0">
          <button
            type="button"
            onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            className="btn-secondary"
          >
            {step === 1 ? t('common.cancel') : t('common.back')}
          </button>
          {step < 3 ? (
            <button
              type="button"
              disabled={!canNext()}
              onClick={() => setStep(step + 1)}
              className="btn-primary disabled:opacity-50"
            >
              {t('common.next')}
            </button>
          ) : (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="btn-primary inline-flex items-center gap-2"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('promotions.saveSale')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function parseNonNegative(raw: string): number | null {
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState(() => (Number.isFinite(value) ? String(value) : ''));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(Number.isFinite(value) ? String(value) : '');
    }
  }, [value]);

  const commit = (raw: string) => {
    const next = parseNonNegative(raw) ?? 0;
    onChange(next);
    setDraft(String(next));
  };

  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="mt-1 w-full border rounded-lg px-3 py-2"
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
          setDraft(raw);
          const parsed = parseNonNegative(raw);
          if (parsed !== null) onChange(parsed);
        }}
        onBlur={() => {
          focusedRef.current = false;
          commit(draft);
        }}
      />
    </label>
  );
}
